import _t, {_} from 'i18n'
import PropTypes from 'prop-types'
import React, {cloneElement, useMemo, useState} from 'react'
import {Button, Form, Modal} from 'react-bootstrap'

const TranslationInput = ({
  children,
  type = 'input',
  values = {},
  handleChanges,
  availableLocales = ['en'],
  buttonLabel = _`Traduzir`,
  buttonStyle = {},
  labelField = '',
}) => {
  const [show, setShow] = useState(false)
  const [localValues, setLocalValues] = useState(() => {
    const base = {...values}
    availableLocales.forEach((l) => {
      if (typeof base[l] !== 'string') base[l] = ''
    })
    return base
  })

  const fieldName = useMemo(() => {
    if (labelField) {
      return labelField
    }

    if (!React.isValidElement(children)) return labelField
    return children.props?.name || children.props?.id || ''
  }, [children, labelField])

  const wrappedChild = useMemo(() => {
    if (!React.isValidElement(children)) return children
    return cloneElement(children, {})
  }, [children])

  const handleOpen = () => {
    const fresh = {...values}
    availableLocales.forEach((l) => {
      if (typeof fresh[l] !== 'string') fresh[l] = ''
    })
    setLocalValues(fresh)
    setShow(true)
  }

  const handleClose = () => setShow(false)

  const handleLocaleChange = (locale, text) => {
    setLocalValues((prev) => ({...prev, [locale]: text}))
  }

  const handleSave = () => {
    if (typeof handleChanges === 'function') {
      handleChanges({...localValues})
    }
    setShow(false)
  }

  // UI Helpers
  const labelFromLocale = (locale) => {
    const map = {en: _`English`, pt: _`Português`, es: _`Español`, fr: _`Français`}
    return map[locale] || locale.toUpperCase()
  }

  return (
    <div style={{position: 'relative'}}>
      {wrappedChild}

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginTop: 6,
        }}>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleOpen}
          style={{...buttonStyle}}
          title={_`Abrir editor de traduções`}>
          🌐 {buttonLabel}
        </Button>
      </div>

      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{fieldName ? _t('Traduções para "{{field}}"', {field: fieldName}) : _`Traduções`}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {availableLocales.map((locale) => (
            <div key={locale} style={{marginBottom: 16}}>
              <Form.Label>
                {labelFromLocale(locale)} ({locale})
              </Form.Label>
              {type === 'textarea' ? (
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={localValues[locale] ?? ''}
                  onChange={(e) => handleLocaleChange(locale, e.target.value)}
                  placeholder={_t('Tradução ({{locale}})', {locale: locale.toUpperCase()})}
                />
              ) : (
                <Form.Control
                  type="text"
                  value={localValues[locale] ?? ''}
                  onChange={(e) => handleLocaleChange(locale, e.target.value)}
                  placeholder={_t('Tradução ({{locale}})', {locale: locale.toUpperCase()})}
                />
              )}
            </div>
          ))}

          <div style={{fontSize: 12, color: '#6c757d'}}>
            {_`Dica: deixe em branco se não houver tradução para algum idioma. O conteúdo padrão (ex.: PT-BR) continua vindo do campo original.`}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose}>
            {_`Cancelar`}
          </Button>
          <Button onClick={handleSave}>{_`Salvar traduções`}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

TranslationInput.propTypes = {
  children: PropTypes.node.isRequired,
  type: PropTypes.oneOf(['input', 'textarea']),
  values: PropTypes.object,
  handleChanges: PropTypes.func.isRequired,
  availableLocales: PropTypes.arrayOf(PropTypes.string),
  buttonLabel: PropTypes.string,
  buttonStyle: PropTypes.object,
}

export default TranslationInput
